import firebaseService from "@/services/firebase.service";
import storageService from "@/services/storage.service";
import utilService from "@/services/util.service";
import axios from "axios";
import axiosRetry from "axios-retry";
import { TYPE } from "vue-toastification";
import {
  getStoredKeycloakRefreshToken,
  isKeycloakEnabled,
  persistKeycloakTokens,
  refreshKeycloakAccessToken,
} from "@/services/keycloak.service";

// Event API has a different base URL
const baseURL = process.env.VUE_APP_EVENT_API_URL 
  ? "https://" + process.env.VUE_APP_EVENT_API_URL + "/api"
  : "https://event-dev.traderverse.io/api";

const instance = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

let keycloakRefreshInFlight: Promise<string | null> | null = null;
let lastKeycloakRefreshError: unknown = null;

const parseJwtExp = (token: string): number => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return 0;
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return Number(json?.exp || 0);
  } catch {
    return 0;
  }
};

const isJwtExpiredOrNearExpiry = (token: string, skewSeconds = 30): boolean => {
  const exp = parseJwtExp(token);
  if (!exp) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp <= nowSeconds + skewSeconds;
};

const refreshKeycloakTokenIfNeeded = async (): Promise<string | null> => {
  if (!isKeycloakEnabled()) return null;
  const refreshToken = getStoredKeycloakRefreshToken();
  if (!refreshToken) return null;

  if (!keycloakRefreshInFlight) {
    lastKeycloakRefreshError = null;
    keycloakRefreshInFlight = (async () => {
      const refreshed = await refreshKeycloakAccessToken(refreshToken);
      storageService.setToken(refreshed.access_token);
      persistKeycloakTokens({
        idToken: refreshed.id_token,
        refreshToken: refreshed.refresh_token || refreshToken,
      });
      return refreshed.access_token;
    })()
      .catch((error) => {
        lastKeycloakRefreshError = error;
        return null;
      })
      .finally(() => {
        keycloakRefreshInFlight = null;
      });
  }

  return keycloakRefreshInFlight;
};

// Request interceptor to add auth token and check session before making requests
instance.interceptors.request.use(
  async (config) => {
    // Check if we're in logout flow and cancel new requests
    if (utilService.isInLogoutFlow()) {
      return Promise.reject(new axios.Cancel("Request cancelled due to session expiry"));
    }
    
    // Get token from localStorage and add to Authorization header
    let token = localStorage.getItem("token");
    if (token) {
      // Remove quotes if the token is stored as a JSON string
      try {
        token = JSON.parse(token);
      } catch (e) {
        // If parsing fails, use the token as is
      }
      let accessToken = token;
      if (isKeycloakEnabled() && isJwtExpiredOrNearExpiry(accessToken, 45)) {
        const refreshedAccessToken = await refreshKeycloakTokenIfNeeded();
        if (refreshedAccessToken) {
          accessToken = refreshedAccessToken;
        }
      }
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for enhanced error handling
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Don't process errors if we're already in logout flow
    if (utilService.isInLogoutFlow()) {
      return Promise.reject(error);
    }

    // Handle 401 errors
    if (error.response && error.response.status === 401) {
      const originalConfig = error.config;
      if (originalConfig && !originalConfig.__isKeycloakRetryRequest) {
        const refreshedAccessToken = await refreshKeycloakTokenIfNeeded();
        if (refreshedAccessToken) {
          originalConfig.__isKeycloakRetryRequest = true;
          originalConfig.headers = originalConfig.headers || {};
          originalConfig.headers.Authorization = `Bearer ${refreshedAccessToken}`;
          return instance(originalConfig);
        }
      }
      let user = storageService.getUser();
      
      const refreshMessage = String((lastKeycloakRefreshError as any)?.message || "").toLowerCase();
      const shouldHardLogout =
        !isKeycloakEnabled() ||
        refreshMessage.includes("invalid_grant") ||
        refreshMessage.includes("missing refresh token");

      if (originalConfig.url !== "/auth/signin" && user?._id && shouldHardLogout) {
        utilService.handle401Error();
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

axiosRetry(instance, {
  retries: 3, // number of retries
  retryDelay: (retryCount) => {
    return retryCount * 2000; // time interval between retries
  },
  retryCondition: async (error) => {
    // Don't retry if we're in logout flow
    if (utilService.isInLogoutFlow()) {
      return false;
    }

    // if retry condition is not specified, by default idempotent requests are retried
    const originalConfig = error.config;
    let user = storageService.getUser();
    if (originalConfig.url !== "/auth/signin" && error.response && user?._id) {
      if (error.response.status == 401 && originalConfig) {
        const refreshMessage = String((lastKeycloakRefreshError as any)?.message || "").toLowerCase();
        const shouldHardLogout =
          !isKeycloakEnabled() ||
          refreshMessage.includes("invalid_grant") ||
          refreshMessage.includes("missing refresh token");
        // Don't trigger multiple 401 errors
        if (!utilService.isInLogoutFlow() && shouldHardLogout) {
          utilService.handle401Error();
        }
        return false;
      }
    }
    return error.response!.status === 503;
  },
});

export default instance;

export const eventAPI = instance;

